import { useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { LoaderContext } from '../../../../shared/context/loaderContext';
import { NotificationContext } from '../../../../shared/context/notificationContext';
import { useCm360Api } from 'shared/const/api-backend';
import { reportError } from '../../../../shared/services/raygunService';

const PENDING_CANCELLATION_CODE = 'POLICY_HAS_PENDING_CANCELLATION';
const INVALID_ATTACHMENT_CODE = 'INVALID_ATTACHMENT';
const basePath = 'POLICY_DETAILS.CANCELLATION_MANAGEMENT';

// Plain axios on purpose: the cm360 API key and JWT must not be sent to S3.
const uploadToS3 = async ({ url, fields }, file) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);
  await axios.post(url, formData);
};

// All read/write operations on a policy's cancellation-request (policy_cancellation) row.
const useCancellationRequest = () => {
  const { setLoading } = useContext(LoaderContext);
  const { showNotification } = useContext(NotificationContext);
  const [cancellationRequest, setCancellationRequest] = useState(null);
  const API = useCm360Api();

  // Reads the single row for a policy (pending, done or dismissed).
  // Stays quiet when there is no row (a policy without any cancellation request).
  const fetchCancellationRequest = useCallback(async (policyId) => {
    setLoading(true);
    try {
      const apiUrl = `/ui-proxy/policies/${policyId}/cancellation-request`;
      const response = await API.get(apiUrl);
      const data =
        response.data && !Array.isArray(response.data) ? response.data : null;
      setCancellationRequest(data);
      return data;
    } catch (error) {
      // A missing row is an expected "no request" state, not an error to surface.
      if (error?.response?.status !== 404) {
        reportError(error);
      }
      setCancellationRequest(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitForCancellation = async (policyId, payload) => {
    setLoading(true);
    let result;
    let uploadFailed = false;

    try {
      const files = payload.attachments ?? [];
      const { data: uploads } = await API.post(
        `/ui-proxy/policies/${policyId}/cancellation-request/attachments/uploads`,
        {
          files: files.map((file) => ({
            fileName: file.name,
            size: file.size,
          })),
        },
      );
      try {
        await Promise.all(
          uploads.map((upload, index) => uploadToS3(upload, files[index])),
        );
      } catch (error) {
        uploadFailed = true;
        throw error;
      }

      const apiUrl = `/ui-proxy/policies/${policyId}/cancellation-request`;
      const body = {
        refundAmount:
          payload.refundAmount != null ? +payload.refundAmount : undefined,
        cancellationReason: payload.cancellationReason,
        cancellationRequestDate: payload.cancellationRequestDate,
        methodReceived: payload.methodReceived,
        delayReason: payload.delayReason,
        delayReasonOtherText: payload.delayReasonOtherText,
        refundEligibility: payload.refundEligibility,
        attachments: uploads.map((upload, index) => ({
          uploadId: upload.uploadId,
          fileName: files[index].name,
        })),
      };
      await API.post(apiUrl, body);
      showNotification(`${basePath}.submitSuccess`, 'success');
      result = true;
    } catch (error) {
      const code = error?.response?.data?.code;
      // Backend rejects a second request with 409 when one is already pending/done.
      if (code === PENDING_CANCELLATION_CODE) {
        showNotification(`${basePath}.alreadyPending`, 'error');
      } else if (code === INVALID_ATTACHMENT_CODE) {
        showNotification(`${basePath}.attachmentsInvalid`, 'error');
      } else if (uploadFailed) {
        showNotification(`${basePath}.attachmentsUploadError`, 'error');
      } else {
        showNotification(`${basePath}.submitError`, 'error');
      }
      reportError(error);
    } finally {
      setLoading(false);
      return result;
    }
  };

  const dismissPendingCancellation = async (policyId) => {
    setLoading(true);
    let result;

    try {
      const apiUrl = `/ui-proxy/policies/${policyId}/cancellation-request/dismiss`;
      await API.post(apiUrl, {});
      showNotification(`${basePath}.dismissSuccess`, 'success');
      result = true;
    } catch (error) {
      showNotification(`${basePath}.dismissError`, 'error');
      reportError(error);
    } finally {
      setLoading(false);
      return result;
    }
  };

  const downloadAttachment = async (policyId, attachmentId) => {
    setLoading(true);
    try {
      const { data } = await API.get(
        `/ui-proxy/policies/${policyId}/cancellation-request/attachments/${attachmentId}/download`,
      );
      // The signed URL forces a download (Content-Disposition: attachment), so the page stays open.
      window.location.assign(data.url);
    } catch (error) {
      showNotification(`${basePath}.downloadError`, 'error');
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    cancellationRequest,
    fetchCancellationRequest,
    submitForCancellation,
    dismissPendingCancellation,
    downloadAttachment,
  };
};

export default useCancellationRequest;
